import React from 'react';
import { Spinner } from '../../components/ui';
import ExportButton from '../../components/ExportButton';
import ReportShare from '../../components/ReportShare';
import { fmtDate } from '../../utils/helpers';

// Phone Brief Report: the headline numbers as tiles, then one card per part —
// a phone cannot hold the wide tables, so each line is read down instead of
// across, and the PDF / Excel carry the full tables. Logic: useBrief.
export default function BriefMobile({ vm }) {
  const {
    canView, loading, blocks, toggleBlock, blockLabels, twoSided,
    shownFunctions, memberRows, memberTotals, toggleFunction, pastFunctions,
    invitedByRows, invitedByTotals,
    cardStats,
    moneyRows, moneyTotals, toggleCategory, withEstimates, setWithEstimates,
    bookingRows,
    summary, report, money, reload,
    listFor, openList, closeList, listReport, cardsToPrint, sweetsFunction,
  } = vm;

  if (!canView) return <div className="card card-pad empty">The brief covers the whole event, so only an Event Admin can open it.</div>;

  const Head = ({ k, sub }) => (
    <div className="brief-head">
      <label className="brief-tick">
        <input type="checkbox" checked={blocks[k]} onChange={() => toggleBlock(k)} />
        <strong>{blockLabels[k]}</strong>
      </label>
      {sub && <div className="muted" style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="brief-page brief-mobile">
      <div className="brief-bar">
        <ExportButton report={report} label="⇪ Export brief" className="btn primary sm" />
        <button className="btn light sm" onClick={reload}>↻</button>
      </div>

      <div className="mn-tiles m">
        {summary.map(s => <div key={s.label} className="mn-tile"><span>{s.label}</span><strong>{s.value}</strong></div>)}
      </div>

      {listFor && listReport && <ReportShare {...listReport} onClose={closeList} />}

      {loading ? <Spinner /> : (
        <>
          <div className="card card-pad brief-block">
            <Head k="members" sub={`${memberTotals.persons} persons over the ticked functions${pastFunctions.length ? ` · ${pastFunctions.length} over, left out` : ''}`} />
            {blocks.members && memberRows.map(r => (
              <div key={r.key} className={`brief-line${r.on ? '' : ' brief-off'}`}>
                <label className="brief-tick">
                  <input type="checkbox" checked={r.on} onChange={() => toggleFunction(r.FunctionId)} />
                  <strong>{r.FunctionName}</strong>
                </label>
                <div className="muted" style={{ fontSize: 12 }}>{r.date}{r.time ? ` · ${r.time}` : ''}</div>
                {/* A phone has no right-click: the list is a button. */}
                <span className="brief-list-btn">
                  <button type="button" className="btn light sm"
                    onClick={() => openList('function', r.FunctionId, r.FunctionName)}>📄 Guests</button>
                  <button type="button" className="btn light sm" title="One card per household"
                    onClick={() => openList('household', r.FunctionId, r.FunctionName, 'Cards')}>📇 Cards</button>
                  <button type="button" className="btn light sm" title="One gift per household"
                    onClick={() => openList('household', r.FunctionId, r.FunctionName, 'Gifts')}>🎁 Gifts</button>
                  <button type="button" className="btn light sm" title="One packet per household"
                    onClick={() => openList('household', r.FunctionId, r.FunctionName, 'Sweets')}>🍬 Sweets</button>
                </span>
                <div style={{ fontSize: 13 }}>
                  <strong>{r.persons}</strong> persons (plates) · <strong>{r.cards}</strong> cards / gifts · {r.parties} parties
                  {r.extra ? ` · ${r.extra} on extra cards` : ''}
                  {twoSided && (r.groom || r.bride) ? ` · ${r.groom} groom / ${r.bride} bride` : ''}
                </div>
              </div>
            ))}
            {blocks.members && memberRows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No functions yet.</div>}
          </div>

          <div className="card card-pad brief-block">
            <Head k="invitedBy" sub={`${invitedByRows.length} invited ${invitedByTotals.persons} persons in all`} />
            {blocks.invitedBy && invitedByRows.map(r => (
              <div key={r.key} className="brief-line">
                <strong>{r.name}</strong>{r.isGuest && <span className="chip" style={{ marginLeft: 6 }}>Guest</span>}
                <div className="muted" style={{ fontSize: 12 }}>{r.mobile || '—'}</div>
                <div style={{ fontSize: 13 }}>
                  <strong>{r.persons}</strong> persons · {r.guests} guests · {r.totalCards} cards
                </div>
                <button type="button" className="btn light sm brief-list-btn"
                  onClick={() => openList('adder', r.key, r.name)}>📄 Guest list</button>
                <div className="muted" style={{ fontSize: 12 }}>
                  {shownFunctions.map(f => `${f.FunctionName} ${r.perFn[String(f.FunctionId)] || 0}`).join(' · ')}
                </div>
              </div>
            ))}
            {blocks.invitedBy && invitedByRows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No guests yet.</div>}
          </div>

          <div className="card card-pad brief-block">
            <Head k="cards" sub={`${cardStats.households} households · ${cardsToPrint} cards to print (one gift with each)`} />
            {blocks.cards && (
              <div className="brief-list-btn" style={{ float: 'none', margin: '0 0 8px' }}>
                <button type="button" className="btn light sm"
                  onClick={() => openList('household', null, 'the whole event', 'Cards')}>📇 Card list</button>
                <button type="button" className="btn light sm"
                  onClick={() => openList('household', null, 'the whole event', 'Gifts')}>🎁 Gift list</button>
                {sweetsFunction && (
                  <button type="button" className="btn light sm"
                    onClick={() => openList('household', sweetsFunction.FunctionId, sweetsFunction.FunctionName, 'Sweets')}>🍬 Sweets list</button>
                )}
              </div>
            )}
            {blocks.cards && (
              <>
                {cardStats.rows.map(r => (
                  <div key={r.key} className="brief-line">
                    <strong>{r.name}</strong> <span className="muted" style={{ fontSize: 12 }}>{r.kind}</span>
                    <div style={{ fontSize: 13 }}>
                      <strong>{r.total}</strong> to order · {r.households} households{r.extra ? ` + ${r.extra} extra` : ''}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>{r.forFunctions || '—'}</div>
                  </div>
                ))}
                {cardStats.rows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No card types yet — set them up on the Cards page.</div>}
                {(cardStats.extraCards || cardStats.ownFamily || cardStats.withoutType) ? (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {cardStats.extraCards ? `${cardStats.extraCards} extra cards (${cardStats.extraPersons} persons) · ` : ''}
                    {cardStats.ownFamily ? `${cardStats.ownFamily} own family — no card · ` : ''}
                    {cardStats.withoutType ? `${cardStats.withoutType} households with no card type` : ''}
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="card card-pad brief-block">
            <Head k="money" sub={`${money(moneyTotals.paid)} paid · ${money(moneyTotals.balance)} to pay`} />
            {blocks.money && (
              <>
                <label className="brief-tick" style={{ fontSize: 12.5 }}>
                  <input type="checkbox" checked={withEstimates} onChange={e => setWithEstimates(e.target.checked)} />
                  Count amounts still marked “estimate”
                </label>
                {moneyRows.map(r => (
                  <div key={r.key} className={`brief-line${r.on ? '' : ' brief-off'}`}>
                    <label className="brief-tick">
                      <input type="checkbox" checked={r.on} onChange={() => toggleCategory(r.key)} />
                      <strong>{r.category}</strong>
                    </label>
                    <div style={{ fontSize: 13 }}>
                      {money(r.total)} · paid {money(r.paid)} · <strong>{money(r.balance)}</strong> to pay
                      {r.estimates ? ` · ${r.estimates} est.` : ''}
                      {r.nextDue ? <span className={r.dueSoon > 0 ? 'mn-due' : 'muted'}> · due {fmtDate(r.nextDue)}</span> : null}
                    </div>
                  </div>
                ))}
                {moneyRows.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Nothing booked yet.</div>}
                <div className="brief-line">
                  <strong>Total{moneyTotals.estimates ? ' (incl. estimates)' : ''}</strong>
                  <div style={{ fontSize: 13 }}>
                    {money(moneyTotals.total)} · paid {money(moneyTotals.paid)} · <strong>{money(moneyTotals.balance)}</strong> to pay
                    {moneyTotals.dueSoon > 0 && <div className="mn-due">{money(moneyTotals.dueSoon)} due in 7 days</div>}
                  </div>
                </div>
              </>
            )}
          </div>

          {bookingRows.length > 0 && (
            <div className="card card-pad brief-block">
              <Head k="bookings" />
              {blocks.bookings && bookingRows.map(r => (
                <div key={r.key} className="brief-line">
                  <strong>{r.item} <span className="muted" style={{ fontWeight: 400 }}>· {r.forFn}</span></strong>
                  <div style={{ fontSize: 13 }}>
                    {r.rule === 'minimum' ? 'min' : 'ordered'} {r.booked} · now {r.live} {r.follows} · charged <strong>{r.charged}</strong> = {money(r.amount)}
                    <div className={/short/.test(r.note) ? 'mn-due' : 'muted'}>{r.booking} · {r.note}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
